<template>
  <div>
    <div id="main-container">
      <div class="data-list-container">
        <vs-table ref="table" class="pb-5" search pagination :max-items="itemsPerPage" :data="sortableProtocols">
          <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between">
            <vs-button color="primary" type="border" icon-after icon="add" @click="showAddNew = true">Add New Protocol</vs-button>
            <vs-dropdown vs-trigger-click class="cursor-pointer mb-4 mr-4">
              <div class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium">
                <span class="mr-2">{{ currentPage * itemsPerPage - (itemsPerPage - 1) }} - {{ protocols.length - currentPage * itemsPerPage > 0 ? currentPage * itemsPerPage : protocols.length }} of {{ protocols.length }}</span>
                <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
              </div>
              <vs-dropdown-menu>
                <vs-dropdown-item @click="itemsPerPage=15"><span>15</span></vs-dropdown-item>
                <vs-dropdown-item @click="itemsPerPage=25"><span>25</span></vs-dropdown-item>
                <vs-dropdown-item @click="itemsPerPage=50"><span>50</span></vs-dropdown-item>
                <vs-dropdown-item @click="itemsPerPage=100"><span>100</span></vs-dropdown-item>
              </vs-dropdown-menu>
            </vs-dropdown>
          </div>

          <template slot="thead">
            <vs-th sort-key="id">ID</vs-th>
            <vs-th sort-key="name_internal">Name (Internal)</vs-th>
            <vs-th sort-key="category_name">Category</vs-th>
            <vs-th sort-key="translations_count">Translations</vs-th>
            <vs-th sort-key="contents_count">Content</vs-th>
          </template>

          <template slot-scope="{data}">
            <tbody :data="protocol" :key="indextr" v-for="(protocol, indextr) in data" @click="goToDetail(protocol.id)">
              <vs-tr class="cursor-pointer">
                <vs-td>
                  <p class="font-medium truncate">{{ protocol.id }}</p>
                </vs-td>
                <vs-td>
                  <p class="font-medium truncate">{{ protocol.name_internal }}</p>
                </vs-td>
                <vs-td>
                  <p class="font-medium truncate">{{ protocol.category ? protocol.category.name_internal : '—' }}</p>
                </vs-td>
                <vs-td>
                  <p class="font-medium truncate">{{ protocol.translationCount || 0 }}</p>
                </vs-td>
                <vs-td>
                  <p class="font-medium truncate">{{ protocol.contentCount || 0 }}</p>
                </vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>
      </div>
    </div>

    <vs-prompt buttons-hidden title="Add Protocol" :active.sync="showAddNew" @close="close">
      <vs-progress v-if="loading" indeterminate color="primary"></vs-progress>
      <div class="vx-row mb-3">
        <div class="vx-col w-full">
          <vs-input
            label="Name (Internal)"
            class="w-full mb-3"
            v-model="formData.name_internal"
            :danger="!!errors.name_internal"
            :danger-text="errors.name_internal || null"
            val-icon-danger="error_outline"
          />
        </div>
      </div>
      <div class="vx-row mb-3">
        <div class="vx-col w-full">
          <label class="vs-input--label">Category</label>
          <vs-select class="w-full" v-model="formData.category_id">
            <vs-select-item
              v-for="cat in categories"
              :key="cat.id"
              :value="cat.id"
              :text="cat.name_internal"
            />
          </vs-select>
          <span v-if="errors.category_id" class="text-danger text-sm">{{ errors.category_id }}</span>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full mb-4">
          <vs-button class="mx-1 w-full" @click="createProtocol" :disabled="loading">Create</vs-button>
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import api from '@/http/api';

export default {
  data() {
    return {
      itemsPerPage: 25,
      isMounted: false,
      showAddNew: false,
      loading: false,
      protocols: [],
      categories: [],
      formData: {
        name_internal: '',
        category_id: null
      },
      errors: {}
    }
  },
  mounted() {
    this.isMounted = true;
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const [protocols, categories] = await Promise.all([
        api.getProtocols(),
        api.getProtocolCategories()
      ]);
      this.protocols = protocols;
      this.categories = categories;
    },
    async createProtocol() {
      if (this.validateForm()) return;

      this.loading = true;
      const result = await api.createProtocol({
        name_internal: this.formData.name_internal.trim(),
        category_id: this.formData.category_id
      });
      this.loading = false;

      if (!result) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to create protocol.',
          color: 'danger',
          icon: 'error_outline'
        });
        return;
      }

      await this.fetchData();
      this.showAddNew = false;
      this.$vs.notify({
        title: 'Success',
        text: `Added ${this.formData.name_internal} successfully!`,
        color: 'success',
        icon: 'check_circle_outline'
      });
      this.clearForm();
    },
    validateForm() {
      this.errors = {};
      if (!this.formData.name_internal || !this.formData.name_internal.trim().length) {
        this.errors.name_internal = 'Name is required';
      }
      if (!this.formData.category_id) {
        this.errors.category_id = 'Category is required';
      }
      return Object.keys(this.errors).length > 0;
    },
    clearForm() {
      this.formData = { name_internal: '', category_id: null };
      this.errors = {};
    },
    close() {
      this.clearForm();
      this.showAddNew = false;
    },
    goToDetail(id) {
      this.$router.push(`/content/protocols/${id}`).catch(() => {});
    }
  },
  computed: {
    currentPage() {
      if (this.isMounted) {
        return this.$refs.table.currentx;
      }
      return 0;
    },
    sortableProtocols() {
      return this.protocols.map(p => ({
        ...p,
        category_name: p.category ? p.category.name_internal : '',
        translations_count: p.translationCount || 0,
        contents_count: p.contentCount || 0
      }));
    }
  }
}
</script>
